import React, { useContext, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { IPWhitelist } from '../../../types/ipWhitelist';
import ModalWrapper from '../../../common/ModalWrapper';
import ConfirmationModal from '../../../common/ConfirmationModal';
import { AuthContext } from '../../../auth/authContext';
import { useIPWhitelist, useCreateOrUpdateIP, useDeleteIP, useToggleActiveIP } from '../../settings/hooks/useIPWhitelist';

const WhitelistIPs = () => {
    const { id: userId } = useContext(AuthContext);
    const { data: items = [], isLoading: loading, refetch } = useIPWhitelist(userId ?? '');
    const [search, setSearch] = useState('');
    const { username } = useContext(AuthContext);
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState<IPWhitelist | null>(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<IPWhitelist | null>(null);
    const createOrUpdateMutation = useCreateOrUpdateIP();
    const deleteMutation = useDeleteIP(userId ?? '');
    const toggleActiveMutation = useToggleActiveIP(userId ?? '');

    const openCreate = () => {
        setEditing(null);
        setModalOpen(true);
    };

    const openEdit = (id: string) => {
        const item = items.find(i => i.id === id) || null;
        setEditing(item);
        setModalOpen(true);
    };

    const handleDeleteRequest = (item: IPWhitelist) => {
        setItemToDelete(item);
        setShowDeleteConfirm(true);
    };

    const onDelete = () => {
        if (!itemToDelete) return;
        setShowDeleteConfirm(false);
        deleteMutation.mutate(itemToDelete.id);
    };

    const saveItem = async (payload: IPWhitelist) => {
        createOrUpdateMutation.mutate({ ...payload, apiKey: userId ?? "" }, {
            onSuccess: () => {
                setModalOpen(false);
            }
        });
    };

    const toggleActive = (id: string, active: boolean) => {
        toggleActiveMutation.mutate({ id, active });
    };

    const filtered = (items as IPWhitelist[]).filter(it => {
        if (!search) return true;
        const s = search.toLowerCase();
        return it.ipAddress.toLowerCase().includes(s);
    });

    return (
        <div className="app-content main-content mt-0">
            <div className="side-app">
                <div className="main-container container-fluid">
                    <div className="page-header">
                        <div>
                            <h1 className="page-title">IP Whitelist</h1>
                            <p className="text-muted">Manage IP addresses allowed to access the API.</p>
                        </div>
                        <div className="ms-auto pageheader-btn">
                            <Link to="/settings" className="btn btn-dark me-2 hover:bg-darken-400">
                                <i className="fe fe-arrow-left"></i> Back to Settings
                            </Link>
                            <button className="btn btn-light me-2" onClick={() => refetch()} disabled={loading}><i className="fe fe-refresh-cw"></i> Refresh</button>
                            <button className="btn btn-primary" onClick={openCreate}><i className="fe fe-plus"></i> Add IP</button>
                        </div>
                    </div>

                    <div className="card mb-3">
                        <div className="card-body">
                            <div className="row g-2 mb-3 align-items-center">
                                <div className="col-md-4">
                                    <div className="input-group">
                                        <span className="input-group-text bg-transparent border-end-0"><i className="fe fe-search"></i></span>
                                        <input className="form-control border-start-0" placeholder="Search by IP" value={search} onChange={e => setSearch(e.target.value)} />
                                    </div>
                                </div>
                            </div>

                            {loading && <div className="text-center p-3">Loading...</div>}

                            {!loading && items.length === 0 && (
                                <div className="text-center p-5">
                                    <i className="fe fe-shield fs-1 text-muted mb-3"></i>
                                    <h4 className="text-muted">No whitelisted IPs yet</h4>
                                    <p className="text-muted">Add IP addresses that should be allowed to access the API.</p>
                                    <button className="btn btn-primary" onClick={openCreate}><i className="fe fe-plus"></i> Add IP</button>
                                </div>
                            )}

                            {!loading && items.length > 0 && (
                                <div className="table-responsive">
                                    <table className="table table-striped">
                                        <thead>
                                            <tr>
                                                <th>IP Address</th>
                                                <th>Is Active</th>
                                                <th className="text-end">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filtered.map(it => (
                                                <tr key={it.id}>
                                                    <td><span style={{ fontFamily: 'monospace' }}>{it.ipAddress}</span></td>
                                                    <td>
                                                        <div className="form-check form-switch">
                                                            <input className="form-check-input" type="checkbox" checked={it.isActive} onChange={e => toggleActive(it.id, e.target.checked)} />
                                                        </div>
                                                    </td>
                                                    <td className="text-end">
                                                        <button className="btn btn-sm btn-outline-primary me-2" onClick={() => openEdit(it.id)}>Edit</button>
                                                        <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteRequest(it)}>Delete</button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>

                    <ModalWrapper show={modalOpen} title={editing ? 'Edit IP' : 'Add IP'} onClose={() => setModalOpen(false)}>
                        <IPForm initial={editing} onCancel={() => setModalOpen(false)} onSaved={saveItem} submitting={createOrUpdateMutation.status === 'pending'} />
                    </ModalWrapper>

                    {itemToDelete && (
                        <ConfirmationModal
                            show={showDeleteConfirm}
                            title="Delete Whitelist Entry"
                            message={`Are you sure you want to delete IP ${itemToDelete.ipAddress}? This action cannot be undone.`}
                            onConfirm={onDelete}
                            onCancel={() => setShowDeleteConfirm(false)}
                            confirmText="Delete"
                            confirmButtonClass="btn-danger"
                            iconClass="fe fe-trash-2 text-danger"
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

type IPFormProps = {
    initial?: IPWhitelist | null;
    onSaved: (p: IPWhitelist) => void;
    onCancel: () => void;
}

interface IPFormExtra extends IPFormProps { submitting?: boolean }
const IPForm: React.FC<IPFormExtra> = ({ initial, onSaved, onCancel, submitting }) => {
    const [ipAddress, setIpAddress] = useState(initial?.ipAddress || '');

    const [isActive, setIsActive] = useState(initial?.isActive ?? true);
    const [errors, setErrors] = useState<{ ipAddress?: string }>({});
    const [internalSubmitting, setInternalSubmitting] = useState(false);

    const ipv4Regex = /^(25[0-5]|2[0-4]\d|1?\d?\d)(\.(25[0-5]|2[0-4]\d|1?\d?\d)){3}$/;
    const ipv6Regex = /^(([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}|(::)|(([0-9a-fA-F]{1,4}:){1,7}:)|(:{1,7}[0-9a-fA-F]{1,4})|(([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}))$/;

    const validate = () => {
        const errs: { ipAddress?: string; description?: string } = {};
        const ip = ipAddress.trim();
        if (!ip) {
            errs.ipAddress = 'IP address is required';
        } else if (!(ipv4Regex.test(ip) || ipv6Regex.test(ip))) {
            errs.ipAddress = 'Enter a valid IPv4 or IPv6 address';
        }
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const onBlurValidate = () => {
        if (ipAddress) validate();
    };

    const onSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!validate()) return;
        if (!submitting) setInternalSubmitting(true);
        const payload: IPWhitelist = {
            id: initial?.id || '',
            apiKey: initial?.apiKey || '',
            ipAddress: ipAddress.trim(),
            description: undefined,
            isActive,
        } as IPWhitelist;
        Promise.resolve(onSaved(payload)).finally(() => setInternalSubmitting(false));
    };

    return (
        <form onSubmit={onSubmit}>
            <div className="mb-3">
                <label className="form-label">IP Address <span className="text-danger">*</span></label>
                <input
                    className={`form-control ${errors.ipAddress ? 'is-invalid' : ipAddress && !errors.ipAddress ? 'is-valid' : ''}`}
                    value={ipAddress}
                    onChange={e => setIpAddress(e.target.value)}
                    onBlur={onBlurValidate}
                    placeholder="e.g. 203.0.113.5"
                    autoFocus={!initial}
                    required
                />
                {errors.ipAddress && <div className="invalid-feedback d-block">{errors.ipAddress}</div>}
                {/* {!errors.ipAddress && ipAddress && (ipv4Regex.test(ipAddress)) && (
                    <div className="form-text text-success"><i className="fe fe-check-circle"></i> Looks good.</div>
                )} */}
            </div>
            {/* Description field removed */}
            {/* <div className="mb-3 mx-1 d-flex align-items-center justify-content-between" style={{ maxWidth: 150 }}>
                <label className="form-label d-flex align-items-center" htmlFor="activeSwitch">
                    <span>{isActive ? 'Active' : 'Inactive'}</span>
                    <i className={`fe ${isActive ? 'fe-check-circle text-success' : 'fe-x-circle text-muted'} ms-2`}></i>
                </label>
                <div className="form-check form-switch">
                    <input
                        className="form-check-input"
                        type="checkbox"
                        checked={isActive}
                        onChange={e => setIsActive(e.target.checked)}
                        id="activeSwitch"
                        style={{ width: 40, height: 22, cursor: 'pointer' }}
                    />
                </div>
            </div> */}
            <div className="text-end">
                <button type="button" className="btn btn-light me-2" onClick={onCancel} disabled={submitting || internalSubmitting}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={submitting || internalSubmitting}>
                    {(submitting || internalSubmitting) && <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>}
                    {initial ? 'Update' : 'Save'}
                </button>
            </div>
        </form>
    );
};

export default WhitelistIPs;
